import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { Bell, Mail, Calendar, Save, Loader2, ChevronDown, ChevronRight, Users, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const EXTERNAL_ROLES = ['vendor', 'labour', 'client', 'consultant', 'architect', 'interior_designer', 'external_designer'];
const ADMIN_ROLES = ['owner', 'admin', 'staff', 'project_manager', 'designer', 'site_engineer'];

export default function NotificationPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Master toggles for external users
  const [externalDaily, setExternalDaily] = useState(true);
  const [externalWeekly, setExternalWeekly] = useState(true);

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/email-summaries/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const prefs = res.data.preferences || [];
      setPreferences(prefs);
      setProjects(res.data.projects || []);

      // Derive external master state from actual data
      const externals = prefs.filter(p => EXTERNAL_ROLES.includes(p.user_role));
      if (externals.length > 0) {
        setExternalDaily(externals.every(p => p.daily_summary_enabled));
        setExternalWeekly(externals.every(p => p.weekly_summary_enabled));
      }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load preferences');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  // Admin individual toggles
  const updateAdminPref = (userId, field, value) => {
    setPreferences(prev => prev.map(p =>
      p.user_id === userId ? { ...p, [field]: value } : p
    ));
    setDirty(true);
  };

  // Admin per-project override
  const updateProjectOverride = (userId, projectId, field, value) => {
    setPreferences(prev => prev.map(p => {
      if (p.user_id !== userId) return p;
      const overrides = { ...p.project_overrides };
      if (!overrides[projectId]) overrides[projectId] = { daily: true, weekly: true };
      overrides[projectId] = { ...overrides[projectId], [field]: value };
      return { ...p, project_overrides: overrides };
    }));
    setDirty(true);
  };

  // Master toggle for ALL admin team
  const toggleAllAdmins = (field, value) => {
    setPreferences(prev => prev.map(p =>
      ADMIN_ROLES.includes(p.user_role) ? { ...p, [field]: value } : p
    ));
    setDirty(true);
  };

  // Master toggle for ALL external (vendors/clients/consultants)
  const toggleAllExternal = (field, value) => {
    if (field === 'daily_summary_enabled') setExternalDaily(value);
    if (field === 'weekly_summary_enabled') setExternalWeekly(value);
    setPreferences(prev => prev.map(p =>
      EXTERNAL_ROLES.includes(p.user_role) ? { ...p, [field]: value } : p
    ));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const updates = preferences.map(p => ({
        user_id: p.user_id,
        daily_summary_enabled: p.daily_summary_enabled,
        weekly_summary_enabled: p.weekly_summary_enabled,
        project_overrides: p.project_overrides || {},
      }));
      await axios.put(`${API}/email-summaries/preferences-bulk`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notification preferences saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const triggerEmail = async (type) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email-summaries/trigger-${type}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${type === 'daily' ? 'Daily' : 'Weekly'} summary emails are being sent`);
    } catch {
      toast.error('Failed to trigger emails');
    }
  };

  const adminMembers = preferences.filter(p => ADMIN_ROLES.includes(p.user_role));
  const adminAllDaily = adminMembers.length > 0 && adminMembers.every(p => p.daily_summary_enabled);
  const adminAllWeekly = adminMembers.length > 0 && adminMembers.every(p => p.weekly_summary_enabled);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="notification-preferences-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-stone-700" />
              Notification Preferences
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              Control daily and weekly project email summaries.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => triggerEmail('daily')} data-testid="trigger-daily-btn">
              <Mail className="w-4 h-4 mr-1" /> Send Daily Now
            </Button>
            <Button variant="outline" size="sm" onClick={() => triggerEmail('weekly')} data-testid="trigger-weekly-btn">
              <Calendar className="w-4 h-4 mr-1" /> Send Weekly Now
            </Button>
            <Button onClick={handleSave} disabled={!dirty || saving} data-testid="save-preferences-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Schedule Info */}
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-6">
          <div className="flex gap-8 text-sm text-stone-600">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span><strong>Daily Summary</strong> — 8:00 AM IST, every day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <span><strong>Weekly Report</strong> — 9:00 AM IST, every Monday</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : (
          <div className="space-y-6">

            {/* ── Vendors, Clients & Consultants: single master toggle ── */}
            <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="external-section">
              <div className="flex items-center justify-between px-5 py-4 bg-stone-50 border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-stone-500" />
                  <div>
                    <h2 className="text-sm font-semibold text-stone-800">Vendors, Clients & Consultants</h2>
                    <p className="text-xs text-stone-400 mt-0.5">
                      One setting for all external users
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-stone-500 w-12">Daily</Label>
                    <Switch
                      checked={externalDaily}
                      onCheckedChange={(v) => toggleAllExternal('daily_summary_enabled', v)}
                      data-testid="external-daily-toggle"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-stone-500 w-14">Weekly</Label>
                    <Switch
                      checked={externalWeekly}
                      onCheckedChange={(v) => toggleAllExternal('weekly_summary_enabled', v)}
                      data-testid="external-weekly-toggle"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Admin Team: master + individual toggles ── */}
            {adminMembers.length > 0 && (
              <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="admin-section">
                {/* Master row for Admin Team */}
                <div className="flex items-center justify-between px-5 py-4 bg-stone-50 border-b border-stone-200">
                  <div className="flex items-center gap-3">
                    <UserCheck className="w-5 h-5 text-stone-500" />
                    <div>
                      <h2 className="text-sm font-semibold text-stone-800">Admin Team</h2>
                      <p className="text-xs text-stone-400 mt-0.5">Master toggle applies to all members below</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-stone-500 w-12">Daily</Label>
                      <Switch
                        checked={adminAllDaily}
                        onCheckedChange={(v) => toggleAllAdmins('daily_summary_enabled', v)}
                        data-testid="admin-master-daily-toggle"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-stone-500 w-14">Weekly</Label>
                      <Switch
                        checked={adminAllWeekly}
                        onCheckedChange={(v) => toggleAllAdmins('weekly_summary_enabled', v)}
                        data-testid="admin-master-weekly-toggle"
                      />
                    </div>
                  </div>
                </div>

                {/* Individual admin rows */}
                <div className="divide-y divide-stone-100">
                  {adminMembers.map(pref => {
                    const isExpanded = expandedUser === pref.user_id;
                    return (
                      <div key={pref.user_id} data-testid={`user-pref-${pref.user_id}`}>
                        <div className="flex items-center px-5 py-3 hover:bg-stone-50/60 transition-colors">
                          <button
                            className="mr-3 text-stone-400 hover:text-stone-600"
                            onClick={() => setExpandedUser(isExpanded ? null : pref.user_id)}
                            data-testid={`expand-${pref.user_id}`}
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-900 truncate">{pref.user_name}</p>
                            <p className="text-xs text-stone-400 truncate">{pref.user_email} &middot; {pref.user_role}</p>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-stone-500 w-12">Daily</Label>
                              <Switch
                                checked={pref.daily_summary_enabled}
                                onCheckedChange={(v) => updateAdminPref(pref.user_id, 'daily_summary_enabled', v)}
                                data-testid={`daily-toggle-${pref.user_id}`}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-stone-500 w-14">Weekly</Label>
                              <Switch
                                checked={pref.weekly_summary_enabled}
                                onCheckedChange={(v) => updateAdminPref(pref.user_id, 'weekly_summary_enabled', v)}
                                data-testid={`weekly-toggle-${pref.user_id}`}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Per-project overrides */}
                        {isExpanded && projects.length > 0 && (
                          <div className="bg-stone-50/50 px-5 py-2 pl-14 border-t border-stone-100">
                            <p className="text-xs text-stone-400 mb-2 font-medium">Per-project overrides</p>
                            <div className="space-y-1">
                              {projects.map(proj => {
                                const ov = pref.project_overrides?.[proj.id] || { daily: true, weekly: true };
                                return (
                                  <div key={proj.id} className="flex items-center py-1.5 px-3 rounded hover:bg-stone-100 transition-colors">
                                    <span className="flex-1 text-sm text-stone-700 truncate">{proj.name}</span>
                                    <div className="flex items-center gap-6">
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs text-stone-400 w-12">Daily</Label>
                                        <Switch
                                          checked={ov.daily}
                                          onCheckedChange={(v) => updateProjectOverride(pref.user_id, proj.id, 'daily', v)}
                                          disabled={!pref.daily_summary_enabled}
                                          data-testid={`proj-daily-${pref.user_id}-${proj.id}`}
                                        />
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs text-stone-400 w-14">Weekly</Label>
                                        <Switch
                                          checked={ov.weekly}
                                          onCheckedChange={(v) => updateProjectOverride(pref.user_id, proj.id, 'weekly', v)}
                                          disabled={!pref.weekly_summary_enabled}
                                          data-testid={`proj-weekly-${pref.user_id}-${proj.id}`}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
